import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import axios from "axios";

const SUPPORTED_CURRENCIES = [
  { code: "CAD", label: "CAD", flag: "https://flagcdn.com/w20/ca.png" },
  { code: "USD", label: "USD", flag: "https://flagcdn.com/w20/us.png" },
];

const DEFAULT_CURRENCY_SETTINGS = {
  defaultCurrencyCode: "CAD",
  enabledCurrencyCodes: ["CAD"],
  exchangeRates: {},
};

const normalizeCurrencySettings = (settings = {}) => {
  const defaultCurrencyCode = SUPPORTED_CURRENCIES.some((c) => c.code === settings.defaultCurrencyCode)
    ? settings.defaultCurrencyCode
    : DEFAULT_CURRENCY_SETTINGS.defaultCurrencyCode;
  const enabled = Array.isArray(settings.enabledCurrencyCodes)
    ? settings.enabledCurrencyCodes.filter((code) => SUPPORTED_CURRENCIES.some((c) => c.code === code))
    : [];
  const enabledCurrencyCodes = Array.from(new Set([defaultCurrencyCode, ...enabled]));
  const exchangeRates = Object.entries(settings.exchangeRates || {}).reduce((acc, [key, value]) => {
    const n = Number(value);
    if (Number.isFinite(n) && n > 0) acc[key] = n;
    return acc;
  }, {});

  return { defaultCurrencyCode, enabledCurrencyCodes, exchangeRates };
};

const CurrencyOption = ({ currency }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
    <img src={currency.flag} alt="" width={20} height={14} style={{ objectFit: "cover", borderRadius: 2 }} />
    <span>{currency.label}</span>
  </Box>
);

export default function CurrencySettingsTab({ instanceUniqueId }) {
  const [instanceId, setInstanceId] = useState(null);
  const [draft, setDraft] = useState(DEFAULT_CURRENCY_SETTINGS);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const SERVER = process.env.REACT_APP_NODEJS_SERVER_URL;
  const headers = { headers: { accessToken: localStorage.getItem("accessToken") } };

  const fetchInstanceId = async () => {
    if (!instanceUniqueId) return;
    const res = await axios.get(`${SERVER}/instances/byinstanceUniqueId/${instanceUniqueId}`, headers);
    setInstanceId(res.data?.id || null);
  };

  const fetchCurrencySettings = async (targetInstanceId = instanceId) => {
    if (!targetInstanceId) return;
    const res = await axios.get(`${SERVER}/instances/${targetInstanceId}/defaults`, headers);
    setDraft(normalizeCurrencySettings(res.data?.defaults?.currencySettings));
  };

  useEffect(() => {
    fetchInstanceId();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instanceUniqueId]);

  useEffect(() => {
    if (instanceId) fetchCurrencySettings(instanceId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instanceId]);

  const updateDefaultCurrency = (defaultCurrencyCode) => {
    setDraft((prev) => ({
      ...prev,
      defaultCurrencyCode,
      enabledCurrencyCodes: Array.from(new Set([defaultCurrencyCode, ...(prev.enabledCurrencyCodes || [])])),
    }));
  };

  const toggleEnabledCurrency = (code) => {
    setDraft((prev) => {
      if (code === prev.defaultCurrencyCode) return prev;
      const enabled = new Set(prev.enabledCurrencyCodes || []);
      if (enabled.has(code)) enabled.delete(code);
      else enabled.add(code);
      enabled.add(prev.defaultCurrencyCode);
      return { ...prev, enabledCurrencyCodes: Array.from(enabled) };
    });
  };

  const updateRate = (fromCode, toCode, value) => {
    const key = `${fromCode}:${toCode}`;
    setDraft((prev) => ({
      ...prev,
      exchangeRates: { ...prev.exchangeRates, [key]: value },
    }));
  };

  const rateRows = useMemo(() => {
    return (draft.enabledCurrencyCodes || [])
      .filter((code) => code !== draft.defaultCurrencyCode)
      .map((code) => ({ from: code, to: draft.defaultCurrencyCode, key: `${code}:${draft.defaultCurrencyCode}` }));
  }, [draft.defaultCurrencyCode, draft.enabledCurrencyCodes]);

  const canSave = useMemo(() => {
    if (!draft.defaultCurrencyCode) return false;
    if (!(draft.enabledCurrencyCodes || []).includes(draft.defaultCurrencyCode)) return false;
    return rateRows.every((row) => {
      const n = Number(draft.exchangeRates?.[row.key]);
      return Number.isFinite(n) && n > 0;
    });
  }, [draft, rateRows]);

  const save = async () => {
    if (!instanceId || !canSave) return;
    setSaving(true);
    setError("");
    try {
      const enabled = (draft.enabledCurrencyCodes || []).filter((code) =>
        SUPPORTED_CURRENCIES.some((currency) => currency.code === code)
      );
      const exchangeRates = rateRows.reduce((acc, row) => {
        acc[row.key] = Number(draft.exchangeRates?.[row.key]);
        return acc;
      }, {});
      await axios.put(
        `${SERVER}/instances/${instanceId}/defaults`,
        {
          currencySettings: {
            defaultCurrencyCode: draft.defaultCurrencyCode,
            enabledCurrencyCodes: Array.from(new Set([draft.defaultCurrencyCode, ...enabled])),
            exchangeRates,
          },
        },
        headers
      );
      await fetchCurrencySettings(instanceId);
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to save currency settings.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: 2, display: "grid", gap: 2 }}>
      <Box>
        <Typography variant="h6" sx={{ fontWeight: 800 }}>
          Currency
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.8 }}>
          Configure instance-level currency availability and manual exchange rates for future pricing tools.
        </Typography>
      </Box>

      <Paper variant="outlined" sx={{ p: 2, display: "grid", gap: 2 }}>
        <FormControl size="small" sx={{ maxWidth: 280 }}>
          <InputLabel id="default-currency-label">Default Currency</InputLabel>
          <Select
            labelId="default-currency-label"
            label="Default Currency"
            value={draft.defaultCurrencyCode}
            onChange={(e) => updateDefaultCurrency(e.target.value)}
            renderValue={(value) => <CurrencyOption currency={SUPPORTED_CURRENCIES.find((c) => c.code === value)} />}
          >
            {SUPPORTED_CURRENCIES.map((currency) => (
              <MenuItem key={currency.code} value={currency.code}>
                <CurrencyOption currency={currency} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
            Enabled Currencies
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {SUPPORTED_CURRENCIES.map((currency) => {
              const isDefault = currency.code === draft.defaultCurrencyCode;
              const checked = (draft.enabledCurrencyCodes || []).includes(currency.code);
              return (
                <FormControlLabel
                  key={currency.code}
                  control={(
                    <Checkbox
                      checked={checked}
                      disabled={isDefault}
                      onChange={() => toggleEnabledCurrency(currency.code)}
                    />
                  )}
                  label={<CurrencyOption currency={currency} />}
                />
              );
            })}
          </Box>
          <FormHelperText>The default currency must always remain enabled.</FormHelperText>
        </Box>

        <Box sx={{ display: "grid", gap: 1.5 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            Exchange Rates
          </Typography>
          {rateRows.length === 0 ? (
            <Typography variant="body2" sx={{ opacity: 0.75 }}>
              Enable another currency to configure exchange rates.
            </Typography>
          ) : rateRows.map((row) => (
            <TextField
              key={row.key}
              size="small"
              type="number"
              label={`${row.from} → ${row.to} rate`}
              value={draft.exchangeRates?.[row.key] ?? ""}
              onChange={(e) => updateRate(row.from, row.to, e.target.value)}
              inputProps={{ min: 0.0001, step: "0.0001" }}
              sx={{ maxWidth: 280 }}
              error={draft.exchangeRates?.[row.key] !== "" && !(Number(draft.exchangeRates?.[row.key]) > 0)}
              helperText={`${row.from} to ${row.to}, for example ${row.to === "CAD" ? "1.40" : "0.71"}`}
            />
          ))}
          <Typography variant="caption" sx={{ opacity: 0.75 }}>
            Rates are manually configured for now. Automatic daily updates will be added later.
          </Typography>
        </Box>
      </Paper>

      {error && <Typography color="error" variant="body2">{error}</Typography>}

      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <Button variant="contained" onClick={save} disabled={!canSave || saving}>
          {saving ? "Saving..." : "Save"}
        </Button>
      </Box>
    </Box>
  );
}
